import { Alert, Box, DialogContent, styled, Typography } from 'opensolar-ui'
import { findProjectLevelTemplateByOrgLevelTemplate } from 'projectSections/hooks/useTemplateCards'
import React from 'react'
import { Loading, useTranslate } from 'react-admin'
import type { ProposalDataType } from 'types/proposals'
import type { ProposalTemplateType } from 'types/proposalTemplate'
import { ActionType, EventAction, SystemActions } from 'types/tracking'
import { CardRenderData, useTemplateCards } from '../../../hooks/useTemplateCards'
import { getProposalV2DesignStatus } from '../utils'
import ClassicTemplateCard from './ClassicTemplateCard'
import DefaultTemplateCard from './DefaultTemplateCard'
import CommercialPropertyProposalAlert from './proposalAlerts/CommercialPropertyProposalAlert'
import SystemLimitProposalAlert from './proposalAlerts/SystemLimitProposalAlert'
import { getProposalV2TemplateLibraryCardKey } from './proposalTemplateCardKey'
import ProposalV2TemplateCard from './ProposalV2TemplateCard'
import { TemplateLibraryCardGrid } from './TemplateLibraryDialog'

// Keep only the loading modal styled component
const StyledLoadingModal = styled('div')({
  '& .loading-modal': {
    height: '300px',
    display: 'flex',
    ' h1': {
      display: 'none',
    },
  },
})

export enum TemplateLibraryTypeEnum {
  proposalV2_grapes_swipe = 'proposalV2_grapes_swipe',
  Classic = 'classic',
}

type AlertSectionProps = {
  isValidProjectType: boolean
  isSystemLimitExceeded: boolean
}

const AlertSection: React.FC<AlertSectionProps> = ({ isValidProjectType, isSystemLimitExceeded }) => {
  if (!isValidProjectType) {
    return (
      <Box style={{ padding: '24px 24px 0 24px' }}>
        <CommercialPropertyProposalAlert />
      </Box>
    )
  }

  if (isSystemLimitExceeded) {
    return (
      <Box style={{ padding: '24px 24px 0 24px' }}>
        <SystemLimitProposalAlert />
      </Box>
    )
  }

  return null
}

type TemplateLibraryModalContentProps = {
  handleSelect: (key: TemplateLibraryTypeEnum, value?: ProposalTemplateType) => void
  handleSelectUpdatedCardPendingOverride: (template: ProposalTemplateType) => void
  proposalData: ProposalDataType
  projectLevelSMTemplates?: ProposalTemplateType[]
  orgLevelSMTemplates?: ProposalTemplateType[]
  orgLevelClassicTemplates?: ProposalTemplateType[]
  loading: boolean
  selectedCardKeyExternal?: string
  setPendingOverrideTemplate: (pendingTemplate?: ProposalTemplateType) => void
  trackUserInteraction: (action: EventAction, additionalData?: any) => void
  trackSystemEvent: (action: EventAction, additionalData?: any) => void
}

const TemplateLibraryModalContent = React.forwardRef<HTMLDivElement, TemplateLibraryModalContentProps>(
  (
    {
      handleSelect,
      handleSelectUpdatedCardPendingOverride,
      proposalData,
      projectLevelSMTemplates,
      orgLevelSMTemplates,
      orgLevelClassicTemplates,
      loading,
      selectedCardKeyExternal,
      setPendingOverrideTemplate,
      trackUserInteraction,
      trackSystemEvent,
    },
    ref
  ) => {
    const { isSystemLimitExceeded, isValidProjectType, isValidDesign } = getProposalV2DesignStatus(proposalData)
    const translate = useTranslate()

    const { proposalV2CardsData, classicCardsData } = useTemplateCards(
      projectLevelSMTemplates,
      orgLevelSMTemplates,
      orgLevelClassicTemplates
    )

    // create, select (classic/proposalV2) tempates
    const spawnProposalV2TemplateFromDefault = () => {
      trackUserInteraction(
        { type: ActionType.CLICK },
        {
          eventName: 'Default Template Selected',
          action: 'default_template_selected',
          template_type: 'proposalV2_grapes_swipe',
          placeholder: true,
        }
      )
      handleSelect(TemplateLibraryTypeEnum.proposalV2_grapes_swipe, undefined)
    }

    const selectProposalV2Template = (template: ProposalTemplateType) => {
      const isProjectLevelTemplate = !!template.exclusive_project
      trackUserInteraction(
        { type: ActionType.CLICK },
        {
          eventName: 'Template Card Clicked',
          action: 'template_card_clicked',
          template_id: template.id,
          template_type: 'proposalV2_grapes_swipe',
          template_name: template.title,
          is_updated: template.is_updated,
          is_project_level: isProjectLevelTemplate,
          placeholder: false,
        }
      )

      if (template.is_updated && !isProjectLevelTemplate) {
        // Show override prompt - track it
        trackSystemEvent(
          { type: SystemActions.UPDATE },
          {
            eventName: 'Override Prompt Shown',
            action: 'override_prompt_shown',
            template_id: template.id,
            template_name: template.title,
          }
        )

        const projectLevelTemplate = findProjectLevelTemplateByOrgLevelTemplate({
          orgLevelTemplate: template,
          allProjectLevelTemplates: projectLevelSMTemplates || [],
        })

        if (projectLevelTemplate === undefined) {
          throw new Error('Unable to find spawned project custom proposal template')
        }
        // Defer applying until user confirms; same card key rules as handleSelect / grid
        handleSelectUpdatedCardPendingOverride(template)
        setPendingOverrideTemplate({
          ...projectLevelTemplate,
          proposal_v2_config: template.proposal_v2_config,
          settings: template.settings,
        })
        return
      }
      setPendingOverrideTemplate(undefined)
      handleSelect(TemplateLibraryTypeEnum.proposalV2_grapes_swipe, template)
    }

    const selectClassicTemplate = (cardData: CardRenderData) => {
      const isPlaceholder = cardData.isPlaceholderDefault
      const template = isPlaceholder ? null : cardData.templateData

      trackUserInteraction(
        { type: ActionType.CLICK },
        {
          eventName: 'Classic Template Selected',
          action: 'classic_template_selected',
          template_type: 'classic',
          placeholder: isPlaceholder,
          template_id: template?.id,
          template_name: template?.title,
        }
      )
      handleSelect(TemplateLibraryTypeEnum.Classic, template ?? undefined)
    }

    if (loading) {
      return (
        <StyledLoadingModal>
          <Loading loadingPrimary="" loadingSecondary="" className="loading-modal" />
        </StyledLoadingModal>
      )
    }

    return (
      <DialogContent style={{ padding: '0' }}>
        <div ref={ref}>
          <Box padding="0 20px">
            <Typography textVariant="body1">
              {translate('Choose a template to create a proposal for your customer')}
            </Typography>
          </Box>
          <AlertSection isValidProjectType={isValidProjectType} isSystemLimitExceeded={isSystemLimitExceeded} />
          <TemplateLibraryCardGrid padding="20px 20px 0 20px">
            {/* Sales Machine Templates Section */}
            {proposalV2CardsData.map((cardData: CardRenderData) => {
              if (cardData.isPlaceholderDefault === true) {
                return (
                  <div key={cardData.metaData.id}>
                    <DefaultTemplateCard
                      selected={false}
                      onSelect={async () => {
                        const createdInstance = projectLevelSMTemplates?.find((a) => !a.original) // no original
                        if (createdInstance) {
                          selectProposalV2Template(createdInstance)
                        } else {
                          spawnProposalV2TemplateFromDefault()
                        }
                      }}
                      id={cardData.metaData.id}
                      disabled={!isValidDesign}
                    />
                  </div>
                )
              } else {
                const template = cardData.templateData

                const cardKey = getProposalV2TemplateLibraryCardKey(template)
                const isSelected = selectedCardKeyExternal === cardKey

                return (
                  <div key={cardKey}>
                    <ProposalV2TemplateCard
                      template={template}
                      title={cardData.title}
                      selected={isSelected}
                      onSelect={async () => {
                        selectProposalV2Template(template)
                      }}
                      id={String(template.id)}
                      disabled={!isValidDesign}
                    />
                  </div>
                )
              }
            })}

            {/* Classic Templates Section */}
            {classicCardsData.map((template: CardRenderData, index: number) => (
              <div key={index}>
                <ClassicTemplateCard
                  template={template.isPlaceholderDefault ? ({} as ProposalTemplateType) : template.templateData}
                  selected={false}
                  onSelect={async () => selectClassicTemplate(template)}
                  id={template.isPlaceholderDefault ? template.metaData.id : String(template.templateData.id)}
                  disabled={false}
                />
              </div>
            ))}
          </TemplateLibraryCardGrid>
          <Box padding="12px 20px 20px">
            <Alert severity="info">
              {translate(
                'To create and edit your proposal templates go to Control > Purchase Experience > Proposal Template'
              )}
            </Alert>
          </Box>
        </div>
      </DialogContent>
    )
  }
)

TemplateLibraryModalContent.displayName = 'TemplateLibraryModalContent'

export default TemplateLibraryModalContent
